import { useState, useEffect, useContext } from 'react'
import { useT } from '../hooks'
import { recall, remember } from '../cache'
import AuthContext from 'contexte/AuthContext'
import Tableau from 'components/DataComponents/Tableau/Tableau'
import TickerImage from 'components/DataComponents/TickerImage/TickerImage'
import { launchError, launchSuccess } from 'components/notifications'
import { fetchBBookSpreads, updateBBookSpread } from 'services/bBookServices'
import BBookEmpty from './BBookEmpty'

const isSpread = (value) => value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0

// leverage:asset:spreads for the b-book instruments. The full spread, in price units, shared with the A-book. Only the instruments that carry one are listed.
const BBookSpreads = ({ refreshKey }) => {
  const { t } = useT()
  const { user } = useContext(AuthContext)

  const [spreads, setSpreads] = useState(() => recall('spreads') ?? [])
  const [drafts, setDrafts] = useState({})
  const [saving, setSaving] = useState(null)
  const [newTicker, setNewTicker] = useState('')
  const [newSpread, setNewSpread] = useState('')

  useEffect(() => {
    const load = async () => {
      try {
        const data = remember('spreads', await fetchBBookSpreads(user))
        setSpreads(data)
        setDrafts(Object.fromEntries(data.map(({ ticker, spread }) => [ticker, String(spread)])))
      } catch (e) {
        launchError(t('notifications.b_book.spreads.fetch_error'))
      }
    }
    load()
  }, [refreshKey]) // reloaded when an instrument joins or leaves the b-book

  const save = async (ticker, value) => {
    try {
      setSaving(ticker)
      const data = await updateBBookSpread(user, ticker, Number(value))
      setSpreads(prev => prev.map((row) => (row.ticker === ticker ? { ...row, spread: data.spread } : row)))
      setDrafts(prev => ({ ...prev, [ticker]: String(data.spread) }))
      launchSuccess(t('notifications.b_book.spreads.success'))
      return true
    } catch (e) {
      const { error, max } = e?.response?.data || {}
      launchError(error === 'spread_too_large' ? t('notifications.b_book.spreads.too_large', { max }) : t('notifications.b_book.spreads.error'))
      return false
    } finally {
      setSaving(null)
    }
  }

  const addSpread = async () => {
    if (await save(newTicker, newSpread)) {
      setNewTicker('')
      setNewSpread('')
    }
  }

  const withSpread = spreads.filter(({ spread }) => spread > 0) // saving 0 removes the spread, so the row leaves the list
  const withoutSpread = spreads.filter(({ spread }) => !(spread > 0)).sort((a, b) => a.ticker.localeCompare(b.ticker))
  const newValid = newTicker && isSpread(newSpread) && Number(newSpread) > 0

  const tbody = withSpread.map(({ ticker, spread }) => {
    const draft = drafts[ticker] ?? ''
    const valid = isSpread(draft)
    return {
      data: [
        <TickerImage ticker={ticker} />,
        <span className='b_book_num'>{spread}</span>,
        <input
          className={`b_book_exposure_input ${valid ? '' : 'b_book_input_invalid'}`}
          aria-label={t('pages.b_book.spreads.new_label', { ticker })}
          aria-invalid={valid ? undefined : true}
          inputMode='decimal'
          type='number'
          min='0'
          step='any'
          value={draft}
          disabled={saving === ticker}
          onChange={(e) => setDrafts(prev => ({ ...prev, [ticker]: e.target.value }))}
        />,
        <button className='b_book_row_button' disabled={!valid || Number(draft) === spread || saving === ticker} aria-busy={saving === ticker || undefined}
          aria-label={t('pages.b_book.spreads.save_label', { ticker })} onClick={() => save(ticker, draft)}>
          {saving === ticker ? t('pages.b_book.saving') : t('pages.b_book.exposure.save')}
        </button>
      ],
      utils: { id: ticker }
    }
  })

  return (
    <section aria-labelledby='b-book-spreads-title'>
      <div className='b_book_title_component'>
        <h2 id='b-book-spreads-title'>{t('pages.b_book.spreads.title')} <span className='b_book_count'>{withSpread.length}</span></h2>
      </div>
      <p className='b_book_hint'>{t('pages.b_book.spreads.hint')}</p>
      <div className='b_book_card'>
        {withoutSpread.length > 0 && (
          <div className='b_book_toolbar'>
            <div className='b_book_toolbar_field'>
              <label htmlFor='spread-new-ticker'>{t('pages.b_book.spreads.add_ticker')}</label>
              <select id='spread-new-ticker' value={newTicker} onChange={(e) => setNewTicker(e.target.value)}>
                <option value=''>{t('pages.b_book.spreads.add_choose')}</option>
                {withoutSpread.map(({ ticker }) => <option key={ticker} value={ticker}>{ticker}</option>)}
              </select>
            </div>
            <div className='b_book_toolbar_field'>
              <label htmlFor='spread-new-value'>{t('pages.b_book.spreads.add_value')}</label>
              <input id='spread-new-value' className='b_book_exposure_input' type='number' inputMode='decimal' min='0' step='any'
                value={newSpread} onChange={(e) => setNewSpread(e.target.value)} />
            </div>
            <button className='b_book_action_button' disabled={!newValid || saving === newTicker} aria-busy={(saving && saving === newTicker) || undefined} onClick={addSpread}>
              {saving && saving === newTicker ? t('pages.b_book.saving') : t('pages.b_book.spreads.add')}
            </button>
          </div>
        )}
        {tbody.length ? (
          <div className="table-container">
            <div className="table-column">
              <Tableau
                thead={[t('pages.b_book.spreads.table.ticker'), t('pages.b_book.spreads.table.current'), t('pages.b_book.spreads.table.new'), t('pages.b_book.spreads.table.action')]}
                tbody={tbody}
              />
            </div>
          </div>
        ) : (
          <BBookEmpty title={t(spreads.length ? 'pages.b_book.spreads.none_set' : 'pages.b_book.spreads.empty')}
            hint={t(spreads.length ? 'pages.b_book.spreads.none_set_hint' : 'pages.b_book.spreads.empty_hint')} />
        )}
      </div>
    </section>
  )
}

export default BBookSpreads
